import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ShoppingBag, Sparkles, BookOpen, Package, Check, Loader2,
  FlaskConical, ArrowRight, MapPin, Wifi, Lock, MessagesSquare,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import MentorBookingCard from "@/components/MentorBookingCard";
import AddonManage from "@/components/AddonManage";
import usePromoStatus from "@/lib/usePromoStatus";

const ICONS = {
  sparkles: Sparkles,
  book: BookOpen,
  package: Package,
};

const ACCENTS = {
  subscription: "from-amber-500 to-orange-600",
  physical: "from-blue-600 to-blue-800",
};

export default function Shop() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isFree = user && user.tier === "free" && user.role !== "admin";
  const isPremiumUser = user?.tier === "premium" || user?.role === "admin";

  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [busy, setBusy] = useState(null);
  const [residency, setResidency] = useState([]);
  const [myApplications, setMyApplications] = useState([]);

  const refresh = async () => {
    if (isFree) return;
    try {
      const [it, pur, rc, apps] = await Promise.all([
        api.get("/shop/items"),
        api.get("/shop/purchases"),
        api.get("/residency"),
        api.get("/applications/me").catch(() => ({ data: [] })),
      ]);
      setItems(it.data);
      setPurchases(pur.data);
      setResidency(rc.data.programs || []);
      setMyApplications(apps.data || []);
    } catch (err) {
      console.error("[Shop] failed to load shop data:", err);
    }
  };
  useEffect(() => { refresh(); }, [isFree]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (isFree) return <FreeTierShopFunnel />;

  const applicationFor = (programId) =>
    myApplications.find(
      (a) => a.program_id === programId && ["pending", "reviewing", "accepted", "waitlisted"].includes(a.status)
    );

  const buy = async (item) => {
    setBusy(item.id);
    try {
      const { data } = await api.post(`/shop/purchase/${item.id}`);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.success(t("shopPage.toastRecorded", { name: item.name }));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("shopPage.toastError"));
    } finally {
      setBusy(null);
    }
  };

  const alreadyPending = () => false;

  return (
    <div data-testid="shop-root" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
          {t("shopPage.eyebrow")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("shopPage.title")}
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          {t("shopPage.subtitle")}
        </p>
      </div>

      {/* The Residency */}
      {residency.length > 0 && (
        <div data-testid="shop-residency" className="space-y-3">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-1">
                {t("shopPage.residency.eyebrow")}
              </div>
              <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight">
                {t("shopPage.residency.title")}
              </h2>
            </div>
            <Link
              to="/residency"
              data-testid="shop-residency-learn-more"
              className="text-sm font-semibold text-blue-900 hover:text-blue-700 inline-flex items-center gap-1"
            >
              {t("shopPage.residency.details")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {residency.map((p) => {
              const app = applicationFor(p.id);
              const isBench = p.format === "bench_side";
              const isSolo = p.format === "solo_bench";
              const isDark = isBench || isSolo;
              const Icon = isBench ? MapPin : isSolo ? Sparkles : Wifi;
              const hasPrice = p.price !== null && p.price !== undefined;
              return (
                <div
                  key={p.id}
                  data-testid={`shop-residency-${p.format}`}
                  className={`relative overflow-hidden rounded-2xl border p-6 flex flex-col ${
                    isBench
                      ? "bg-slate-950 text-white border-amber-500/25"
                      : isSolo
                        ? "bg-slate-900 text-white border-white/15"
                        : "bg-white text-slate-900 border-slate-200"
                  }`}
                >
                  {isBench && (
                    <>
                      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/15 blur-3xl" />
                      <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-blue-600/20 blur-3xl" />
                    </>
                  )}
                  {isSolo && (
                    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/5 blur-3xl" />
                  )}
                  <div className="relative flex-1 flex flex-col">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        isBench ? "bg-amber-500/15 text-amber-400" :
                        isSolo ? "bg-white/15 text-white" :
                        "bg-blue-100 text-blue-800"
                      }`}>
                        <Icon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-display font-bold text-lg leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                          {p.name}
                        </h3>
                        <p className={`text-[11px] uppercase tracking-widest font-semibold mt-0.5 ${
                          isBench ? "text-amber-400" :
                          isSolo ? "text-amber-300" :
                          "text-blue-800"
                        }`}>
                          {p.tagline}
                        </p>
                      </div>
                    </div>

                    <p className={`mt-4 text-sm leading-relaxed flex-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                      {p.description}
                    </p>

                    {isSolo && (
                      <div
                        data-testid="shop-solo-prereqs"
                        className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs space-y-1"
                      >
                        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300 font-bold">
                          {t("shopPage.residency.prereqs")}
                        </div>
                        <div className="text-white/90">• {t("shopPage.residency.prereqPremium")}</div>
                        <div className="text-white/90">• {t("shopPage.residency.prereqCapital", { amount: "$10,000" })}</div>
                      </div>
                    )}

                    {hasPrice ? (
                      <>
                        <div className="mt-5 flex items-baseline gap-2">
                          <span className={`font-display font-black text-3xl tracking-tight ${
                            isBench ? "text-amber-400" : "text-slate-900"
                          }`}>
                            ${p.price.toLocaleString()}
                          </span>
                          <span className="text-slate-400 line-through text-sm font-medium">
                            ${p.original_price.toLocaleString()}
                          </span>
                        </div>
                        <div className={`text-[11px] mt-1 font-semibold ${
                          isBench ? "text-emerald-400" : "text-emerald-700"
                        }`}>
                          {t("shopPage.residency.saveCurrentIntake", { amount: `$${(p.original_price - p.price).toLocaleString()}` })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mt-5 font-display font-black text-2xl tracking-tight text-white">
                          {t("shopPage.residency.byApplication")}
                        </div>
                        <div className="text-[11px] mt-1 font-semibold text-amber-300">
                          {p.pricing_note || t("shopPage.residency.discussOnApproval")}
                        </div>
                      </>
                    )}

                    {app ? (
                      <div
                        data-testid={`shop-residency-${p.format}-status`}
                        className={`mt-5 rounded-xl border p-3 ${
                          isDark
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                            : "border-emerald-200 bg-emerald-50 text-emerald-800"
                        }`}
                      >
                        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">
                          {t("shopPage.residency.applicationStatus", { status: app.status })}
                        </div>
                        <p className="mt-0.5 text-xs">
                          {t("shopPage.residency.submittedOn", { date: new Date(app.created_at).toLocaleDateString() })}
                        </p>
                      </div>
                    ) : !isPremiumUser ? (
                      <div
                        data-testid={`shop-residency-${p.format}-locked`}
                        className={`mt-5 h-11 rounded-md border inline-flex items-center justify-center gap-2 text-sm font-semibold ${
                          isDark ? "border-white/20 text-white/70" : "border-slate-200 text-slate-500"
                        }`}
                      >
                        <Lock size={14} /> {t("shopPage.residency.premiumOnly")}
                      </div>
                    ) : (
                      <Link
                        to={`/residency/apply?program=${p.id}`}
                        data-testid={`shop-residency-${p.format}-apply`}
                        className="mt-5"
                      >
                        <Button
                          size="lg"
                          className={`w-full h-11 font-semibold ${
                            isBench
                              ? "bg-amber-500 hover:bg-amber-400 text-slate-950"
                              : isSolo
                                ? "bg-white text-slate-950 hover:bg-slate-100"
                                : "bg-blue-900 hover:bg-blue-800 text-white"
                          }`}
                        >
                          {t("shopPage.residency.submit")}
                          <ArrowRight size={14} className="ml-2" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500">
            <FlaskConical size={11} className="inline mr-1 -mt-0.5" />
            {t("shopPage.residency.reviewNote")}
          </p>
        </div>
      )}

      {/* Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((it) => {
          const Icon = ICONS[it.icon] || Package;
          const accent = ACCENTS[it.kind] || ACCENTS.physical;
          const pending = alreadyPending(it.id);
          const isBooking = it.flow === "booking";
          const addonOwned = it.kind === "subscription" && (user?.professor_addon_active || isPremiumUser);
          return (
            <div
              key={it.id}
              data-testid={`shop-item-${it.id}`}
              className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shrink-0 shadow-md text-white`}>
                  <Icon size={22} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-bold text-lg text-slate-900 leading-tight">
                    {it.name}
                  </h3>
                  <p className="text-xs uppercase tracking-widest text-blue-800 font-semibold mt-0.5">
                    {it.tagline}
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm text-slate-600 leading-relaxed flex-1">{it.description}</p>

              <div className="mt-5 flex items-baseline gap-2">
                {it.original_price && it.original_price > it.price && (
                  <span className="text-xl text-slate-400 line-through font-semibold">
                    ${it.original_price.toFixed(0)}
                  </span>
                )}
                <span className="font-display font-black text-3xl text-slate-900">
                  ${it.price.toFixed(2)}
                </span>
                <span className="text-xs text-slate-500 ml-1">
                  {it.billing === "monthly" ? t("shopPage.billing.perMonth") : t("shopPage.billing.oneTime")}
                </span>
                {it.original_price && it.original_price > it.price && (
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
                    <Sparkles size={9} /> {t("shopPage.launchPrice")}
                  </span>
                )}
              </div>

              <Button
                type="button"
                onClick={() => buy(it)}
                disabled={busy === it.id || isBooking || addonOwned}
                data-testid={`shop-buy-${it.id}`}
                className={`mt-5 h-10 w-full ${
                  pending
                    ? "bg-emerald-600 hover:bg-emerald-600 text-white"
                    : "bg-blue-800 hover:bg-blue-700 text-white"
                } ${isBooking || (it.kind === "subscription" && user?.professor_addon_active) ? "hidden" : ""}`}
              >
                {busy === it.id ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> {t("shopPage.actions.processing")}</>
                ) : addonOwned ? (
                  <><Check size={14} className="mr-2" /> {t("shopAddon.includedPremium")}</>
                ) : it.billing === "monthly" ? (
                  <><Sparkles size={14} className="mr-2" /> {t("shopPage.actions.startSubscription")}</>
                ) : (
                  <><ShoppingBag size={14} className="mr-2" /> {t("shopPage.actions.buyNow")}</>
                )}
              </Button>

              {isBooking && <MentorBookingCard item={it} isPremium={isPremiumUser} />}
              {it.kind === "subscription" && user?.professor_addon_active && <AddonManage />}
            </div>
          );
        })}
      </div>

      {/* Purchase history */}
      {purchases.length > 0 && (
        <div data-testid="shop-purchases" className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{t("shopPage.orders.title")}</h3>
          <ul className="divide-y divide-slate-100">
            {purchases.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{p.item_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    ${p.price.toFixed(2)} · {p.billing === "monthly" ? t("shopPage.billing.monthly") : t("shopPage.billing.oneTime")} · {new Date(p.created_at).toLocaleDateString()}
                  </p>
                  {p.fulfillment_status === "shipped" && (
                    <p data-testid={`shop-order-tracking-${p.id}`} className="text-xs text-emerald-700 mt-0.5">{t("shopAddon.shipped", { carrier: p.carrier, tracking: p.tracking_number })}</p>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${
                  p.status === "paid"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {p.status === "paid" ? t("shopPage.orders.paid") : t("shopPage.orders.pending")}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[11px] text-slate-500">
            {t("shopPage.orders.cancellationNote")}
          </p>
        </div>
      )}
    </div>
  );
}

function FreeTierShopFunnel() {
  const { t } = useTranslation();
  const { lp, promo, m } = usePromoStatus();
  const anchor = promo?.premium_anchor?.active ? promo.premium_anchor.was_price : null;
  const perks = t("shopPage.funnel.perks", { returnObjects: true });
  const tierLabel = { basic: "Basic", premium: "Premium" };

  const Card = ({ tier, price, perksList, popular }) => (
    <div
      data-testid={`shop-funnel-${tier}`}
      className={`relative overflow-hidden rounded-2xl p-8 flex flex-col ${
        popular
          ? "bg-slate-950 text-white border border-amber-500/25"
          : "bg-white border border-slate-200"
      }`}
    >
      {popular && (
        <>
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="absolute top-4 right-4 bg-amber-500 text-slate-950 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">
            {t("shopPage.funnel.mostPopular")}
          </div>
        </>
      )}
      <div className="relative flex-1 flex flex-col">
        <div className={`text-xs uppercase tracking-widest font-semibold ${popular ? "text-amber-400" : "text-blue-800"}`}>
          {t(`tier.${tier}`)}
        </div>
        <div className="mt-4 flex items-baseline gap-2 flex-wrap">
          {tier === "premium" && anchor && (
            <span className={`line-through text-2xl font-semibold ${popular ? "text-slate-500" : "text-slate-400"}`}>{m(anchor)}</span>
          )}
          <span className={`font-display font-black text-5xl tracking-tight ${popular ? "text-white" : "text-slate-900"}`}>
            {m(price)}
          </span>
          <span className={`text-sm ${popular ? "text-slate-400" : "text-slate-500"}`}>{t("shopPage.funnel.oneTime")}</span>
        </div>
        <p className={`mt-3 text-sm ${popular ? "text-slate-300" : "text-slate-600"}`}>
          {tier === "basic" ? t("shopPage.funnel.basicSub") : t("shopPage.funnel.premiumSub")}
        </p>
        <ul className={`mt-8 space-y-3 text-sm flex-1 ${popular ? "text-slate-200" : "text-slate-700"}`}>
          {perksList.map((p) => (
            <li key={p} className="flex items-start gap-2">
              <Check size={16} className={`shrink-0 mt-0.5 ${popular ? "text-amber-400" : "text-blue-700"}`} /> {p}
            </li>
          ))}
          <li className={`flex items-start gap-2 rounded-md px-2.5 py-2 border ${
            popular
              ? "bg-amber-500/10 border-amber-500/30 text-white font-medium"
              : "bg-amber-50 border-amber-200 text-slate-900 font-medium"
          }`}>
            <MessagesSquare size={16} className={`shrink-0 mt-0.5 ${popular ? "text-amber-300" : "text-amber-600"}`} />
            {tier === "basic" ? t("shopPage.funnel.discordBasic") : t("shopPage.funnel.discordPremium")}
          </li>
        </ul>
        <Link to={`/checkout/agreement/${tier}`} data-testid={`shop-funnel-${tier}-cta`} className="mt-8">
          <Button
            size="lg"
            className={`w-full h-12 font-semibold ${
              popular
                ? "bg-amber-500 hover:bg-amber-400 text-slate-950"
                : "bg-blue-900 hover:bg-blue-800 text-white"
            }`}
          >
            {t("shopPage.funnel.upgradeTo", { tier: tierLabel[tier] })}
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );

  return (
    <div data-testid="shop-root" className="space-y-8">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] text-slate-500 font-semibold border border-slate-300 rounded-full px-3 py-1.5 bg-slate-50 mb-4">
          <Lock size={11} /> {t("shopPage.funnel.membersOnly")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("shopPage.funnel.title")}
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          {t("shopPage.funnel.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card tier="basic" price={lp.basic} perksList={perks.basic} />
        <Card tier="premium" price={lp.premium} perksList={perks.premium} popular />
      </div>

      <p className="text-xs text-slate-500 max-w-2xl">
        {t("shopPage.funnel.cancelNote")}
      </p>
    </div>
  );
}
